const PATH = Object.freeze({
  // pure paths, no regex substitutions
  root: '/',
  signup: '/signup',
  login: '/login',
  noAccess: '/no-access',
  salonPick: '/salon',
  cy: '/cy/', // this one must end in / to be caught by the router
});

const SEG = Object.freeze({
  // the salon main prefix with a single substitution
  sln: `/salon/:salonId`,
  sid: `/salon/:id`,

  // the main schedule landing page(s)
  schedule: `/salon/:salonId/:view/:dateString/:stylistId?`,

  hr: `/salon/:salonId/hr`,

  // segments attached to the path for the scheduler
  day: `day/:dateString`,
  week: `week/:dateString/:stylistId`,
  eventEdit: `event/edit/:eventId`,
  appointmentEdit: `edit/:appointmentId`,

  // checkout final segments
  checkout: 'checkout',
  checkoutFinish: 'checkout/finish',
  checkoutComplete: 'checkout/complete',
});

// the main export - an enumerated key to path mapping
const LINK = Object.freeze({
  PATH,
  SEG,

  root: PATH.root,

  noAccess: PATH.noAccess,
  salonPick: PATH.salonPick,

  //
  login: PATH.login,
  loginForceReset: `${PATH.login}/forcereset`,
  loginForgot: `${PATH.login}/forgot`,

  //
  sidSalon: `${SEG.sid}`,
  salon: `${SEG.sln}`,
  salonLanding: `${SEG.sln}/landing`,
  salonToday: `${SEG.sln}/day/today`,
  salonOnline: `${SEG.sln}/onlinebooking/settings`,
  salonOnlineBranding: `${SEG.sln}/onlinebooking/branding`,
  salonOnlineTimings: `${SEG.sln}/onlinebooking/booking-timings`,
  salonOnlinePolicy: `${SEG.sln}/onlinebooking/policy`,
  salonOnlineTextAlerts: `${SEG.sln}/onlinebooking/text-alerts`,
  salonOnlineEvouchers: `${SEG.sln}/onlinebooking/e-vouchers`,

  slnTags: `${SEG.sln}/tags`,
  sidTags: `${SEG.sid}/tags`,

  //
  schedule: SEG.schedule,
  scheduleDay: `${SEG.sln}/${SEG.day}`,
  scheduleWeek: `${SEG.sln}/${SEG.week}`,

  scheduleWeekOneStylist: `${SEG.sln}/week/:dateString/:stylistId`,

  scheduleDayClient: `${SEG.sln}/${SEG.day}/client/:clientId`,
  scheduleWeekClient: `${SEG.sln}/${SEG.week}/client/:clientId`,

  scheduleDayClientEdit: `${SEG.sln}/${SEG.day}/client/edit/:clientId`,
  scheduleWeekClientEdit: `${SEG.sln}/${SEG.week}/client/edit/:clientId`,
  //
  bookingWeek: `${SEG.sln}/${SEG.week}/appointment`,
  bookingWeekNew: `${SEG.sln}/${SEG.week}/appointment/new`,
  bookingWeekNewServices: `${SEG.sln}/${SEG.week}/appointment/new/services`,
  bookingWeekOld: `${SEG.sln}/${SEG.week}/appointment/${SEG.appointmentEdit}`,
  bookingWeekLoad: `${SEG.sln}/${SEG.week}/appointment/${SEG.appointmentEdit}/load`,
  bookingWeekSummary: `${SEG.sln}/${SEG.week}/appointment/${SEG.appointmentEdit}/summary`,

  //
  bookingDay: `${SEG.sln}/${SEG.day}/appointment`,
  bookingDayNew: `${SEG.sln}/${SEG.day}/appointment/new`,
  bookingDayNewServices: `${SEG.sln}/${SEG.day}/appointment/new/services`,
  bookingDayOld: `${SEG.sln}/${SEG.day}/appointment/${SEG.appointmentEdit}`,
  bookingDayLoad: `${SEG.sln}/${SEG.day}/appointment/${SEG.appointmentEdit}/load`,
  bookingDaySummary: `${SEG.sln}/${SEG.day}/appointment/${SEG.appointmentEdit}/summary`,

  //
  settingsSalon: `${SEG.sln}/settings/salon`,
  billing: `${SEG.sln}/settings/billing`,
  services: `${SEG.sln}/settings/services`,
  salonHours: `${SEG.sln}/settings/hours`,
  settingStylist: `${SEG.sln}/settings/stylists`,
  settingsPermissions: `${SEG.sln}/settings/permissions`,
  permissions: `${SEG.sln}/settings/permissions/:stylistId?`,
  settingRoles: `${SEG.sln}/settings/roles`,
  settingsCalendar: `${SEG.sid}/settings/calendar-settings`,
  settingsUser: `${SEG.sid}/settings/user_profile`,
  settingsPolicy: `${SEG.sid}/settings/policy`,
  settingsUserNetwork: `${SEG.sid}/settings/user_network`,
  settingWaitlist: `${SEG.sln}/settings/stylists/waitlist`,

  //
  hr: `${SEG.hr}`,
  hrSplash: `${SEG.hr}/splash`,
  hrTargets: `${SEG.hr}/stafftargets`,
  hrCommissions: `${SEG.hr}/commissions`,
  hrTimeoff: `${SEG.hr}/timeoff`,
  hrEditKpi: `${SEG.hr}/edit-kpis`,

  //
  offTimeDay: `${SEG.sln}/${SEG.day}/${SEG.eventEdit}`,
  offTimeWeek: `${SEG.sln}/${SEG.week}/${SEG.eventEdit}`,
  //
  offTimeSplash: `${SEG.sln}/timeoffsplash`,
  //
  sidStaffReports: `${SEG.sid}/staff-reports`,
  slnStaffReportsKpi: `${SEG.sln}/staff-reports/kpi`,
  slnStaffReportsTimeoff: `${SEG.sln}/staff-reports/timeoff`,
  slnStaffReportsTips: `${SEG.sln}/staff-reports/tips`,
  //
  slickPay: `${SEG.sid}/slickpay`,

  //
  checkoutDay: `${SEG.sln}/day/checkout`,
  checkoutWeek: `${SEG.sln}/week`,
  checkoutFinishDay: `${SEG.sln}/day/${SEG.checkoutFinish}`,
  checkoutFinishWeek: `${SEG.sln}/week/:stylistId/${SEG.checkoutFinish}`,
  checkoutCompleteDay: `${SEG.sln}/day/${SEG.checkoutComplete}`,
  checkoutCompleteWeek: `${SEG.sln}/week/:stylistId/${SEG.checkoutComplete}`,
  checkoutStartWeek: `${SEG.sln}/week/:stylistId/${SEG.checkout}`,

  //
  onboarding: `${PATH.signup}`,
  onboardingAddRole: `${PATH.signup}/addrole`,
  onboardingAddService: `${PATH.signup}/addservice`,
  onboardingAddStylist: `${PATH.signup}/addstylist`,
  onboardingCategories: `${PATH.signup}/categories`,
  onboardingEditCategory: `${PATH.signup}/editcategory`,
  onboardingOpeningHours: `${PATH.signup}/openingHours`,
  onboardingPriceType: `${PATH.signup}/salonPriceType`,
  onboardingQuickType: `${PATH.signup}/quickSetupType`,
  onboardingRoles: `${PATH.signup}/roles`,
  onboardingSalon: `${PATH.signup}/salon`,
  onboardingServices: `${PATH.signup}/services`,
  onboardingSms: `${PATH.signup}/smscode`,
  onboardingStylists: `${PATH.signup}/stylists`,
  onboardingType: `${PATH.signup}/salonSetupType`,

  //
  marketing: `${SEG.sln}/communications/marketing`,
  client: `${SEG.sln}/communications/clients/list`,
  campaigns: `${SEG.sln}/communications/campaigns`,
  clientConnect: `${SEG.sln}/communications/clientconnect`,
  mergeClients: `${SEG.sln}/communications/mergeclients`,
  reminders: `${SEG.sln}/communications/reminders`,
  emails: `${SEG.sln}/communications/clients-email`,
  emailsSent: `${SEG.sln}/communications/clients-email-sent`,
  emailsSplash: `${SEG.sln}/communications/clients-email-splash`,
  //
  cashup: `${SEG.sln}/cashup`,

  //
  reports: `${SEG.sln}/reports`,
  reportsBuckets: `${SEG.sln}/reports/buckets`,
  reportsHours: `${SEG.sln}/reports/hours`,
  reportsCommissions: `${SEG.sln}/reports/commissions`,
  //
  vouchersTracking: `${SEG.sln}/vouchers/tracking`,
  //
  stocksManager: `${SEG.sln}/stocks/manager`,
  stocksUsedSold: `${SEG.sln}/stocks/usedSoldOverDateRange`,
  stocksMovement: `${SEG.sln}/stocks/lastStockMovement`,
  stocksHolding: `${SEG.sln}/stocks/holdingValue`,
  stocksOrderReceived: `${SEG.sln}/stocks/orderReceived`,
  stocksCustomAuth: `${SEG.sln}/stocks/customauth`,

  //
  portfolio: `${SEG.sid}/portfolio`,

  //
  support: `${SEG.sid}/support`,
});

export default LINK;
